/**
 * AsistenciaView — Control de Asistencia v1.
 * Muestra el resumen del día por colaborador (presente/ausente)
 * y permite registrar entradas y salidas manualmente.
 *
 * @module presentation/views/AsistenciaView
 */

import { AsistenciaApiClient } from '../../infrastructure/api/AsistenciaApiClient.js';
import { unwrap } from '../../domain/value-objects/CrudResult.js';
import { ColaboradorApiClient } from '../../infrastructure/api/ColaboradorApiClient.js';
import { BusinessApiClient } from '../../infrastructure/api/BusinessApiClient.js';
import { getSelectedBusiness, getSelectedBusinessName } from '../components/BusinessSelector.js';
import { AppConfig } from '../../config.js';
import { attachPullToRefresh } from '../shared/pullToRefresh.js';
import { renderMarcasPropuestas } from '../shared/NxMarcasPropuestas.js';

// ── Helpers ──────────────────────────────────────────────────────────────────

const TZ_COLOMBIA = 'America/Bogota';

// Deuda 4: era una copia del escape; ahora es el compartido.
import { esc } from '../shared/texto.js';

function fmt(ts) {
  if (!ts) return '—';
  const d = new Date(ts);
  return d.toLocaleTimeString('es-CO', { hour: '2-digit', minute: '2-digit', timeZone: TZ_COLOMBIA });
}

function fmtFecha(ts) {
  if (!ts) return '—';
  const d = new Date(ts);
  return d.toLocaleDateString('es-CO', { day: '2-digit', month: 'short', year: 'numeric', timeZone: TZ_COLOMBIA });
}

function fmtHoras(h) {
  if (h == null) return '—';
  const hrs = Math.floor(h);
  const min = Math.round((h - hrs) * 60);
  return `${hrs}h ${min}m`;
}

/**
 * Retorna la fecha de HOY en Colombia como 'YYYY-MM-DD'.
 * Evita el bug de que new Date().toISOString() usa UTC y desde las 19:00 Colombia
 * salta al dia siguiente.
 */
function hoyColombia() {
  const now = new Date();
  const parts = new Intl.DateTimeFormat('en-CA', { timeZone: TZ_COLOMBIA, year: 'numeric', month: '2-digit', day: '2-digit' }).formatToParts(now);
  const y = parts.find(p => p.type === 'year').value;
  const m = parts.find(p => p.type === 'month').value;
  const d = parts.find(p => p.type === 'day').value;
  return `${y}-${m}-${d}`;
}

/**
 * Dado un 'YYYY-MM-DD' en Colombia, retorna el timestamp ISO con offset -05:00
 * para que el backend filtre correctamente sin depender del timezone del cliente.
 */
function rangoColombia(fecha) {
  return {
    fechaDesde: `${fecha}T00:00:00-05:00`,
    fechaHasta: `${fecha}T23:59:59-05:00`
  };
}

// ── Tipos de marcaje (4 tipos) ───────────────────────────────────────────────

const TIPO_INFO = {
  entrada:         { label: 'Entrada',           icon: '⬆', badgeCls: 'badge-ok',    color: 'var(--accent-green)' },
  inicio_almuerzo: { label: 'Salida a almuerzo', icon: '🍽', badgeCls: 'badge-warn',  color: '#9a6700' },
  fin_almuerzo:    { label: 'Regreso almuerzo',  icon: '🔄', badgeCls: 'badge-info',  color: '#0d419d' },
  salida:          { label: 'Salida final',      icon: '⬇', badgeCls: 'badge-danger',color: '#da3633' }
};

// FRONT-VISTAS-EC-V04-T1: rotulo corto del boton de cada fila (el modal sigue diciendo el nombre completo de TIPO_INFO).
const CORTO = { entrada: 'Entrada', inicio_almuerzo: 'Almuerzo', fin_almuerzo: 'Regreso', salida: 'Salida' };

function tipoLabel(tipo) {
  const t = TIPO_INFO[tipo];
  return t ? `${t.icon} ${t.label}` : tipo;
}
function tipoBadgeCls(tipo) {
  return TIPO_INFO[tipo]?.badgeCls || 'badge-muted';
}

function estadoBadge(estado) {
  if (estado === 'presente')
    return `<span class="badge badge-ok">● Presente</span>`;
  return `<span class="badge badge-muted">○ Ausente</span>`;
}

// ── Validacion de secuencia (client-side, T3 PANEL-OPS-DASHBOARDS-001) ──────

/**
 * Devuelve { ok, error } evaluando si el tipo solicitado es logico dado el
 * estado actual del colaborador (fila del resumenHoy).
 *
 * Reglas:
 *  - entrada      → no permitir si ya tiene entrada hoy sin salida (duplicado)
 *  - inicio_almuerzo → requiere entrada previa hoy
 *  - fin_almuerzo    → requiere inicio_almuerzo previo hoy
 *  - salida       → requiere entrada previa hoy
 */
function validarSecuencia(row, tipo) {
  const tieneEntrada       = !!row.ultimaEntrada;
  const tieneSalida        = !!row.ultimaSalida;
  const tieneInicioAlmuerzo = !!row.ultimoInicioAlmuerzo || !!row.inicioAlmuerzo;
  const tieneFinAlmuerzo    = !!row.ultimoFinAlmuerzo    || !!row.finAlmuerzo;

  if (tipo === 'entrada') {
    if (tieneEntrada && !tieneSalida) {
      return { ok: false, error: `Ya hay una entrada registrada hoy (${fmt(row.ultimaEntrada)}) sin salida. Si es error, el admin debe corregir el registro previo.` };
    }
  } else if (tipo === 'salida') {
    if (!tieneEntrada) {
      return { ok: false, error: 'No se puede registrar salida sin una entrada previa del mismo dia. Contacta al admin para corregir.' };
    }
  } else if (tipo === 'inicio_almuerzo') {
    if (!tieneEntrada) {
      return { ok: false, error: 'No se puede registrar inicio de almuerzo sin haber registrado entrada.' };
    }
  } else if (tipo === 'fin_almuerzo') {
    if (!tieneInicioAlmuerzo) {
      return { ok: false, error: 'No se puede registrar fin de almuerzo sin un inicio de almuerzo previo.' };
    }
  }
  return { ok: true };
}

function tieneParCompleto(row) {
  // dia completo esperado: entrada → (inicio_almuerzo → fin_almuerzo) → salida
  // consideramos incompleto si hay entrada pero no salida al dia siguiente,
  // o si hay inicio_almuerzo sin fin_almuerzo.
  const entrada = !!row.ultimaEntrada;
  const salida  = !!row.ultimaSalida;
  const inicioA = !!row.ultimoInicioAlmuerzo || !!row.inicioAlmuerzo;
  const finA    = !!row.ultimoFinAlmuerzo    || !!row.finAlmuerzo;
  if (inicioA && !finA) return false;
  if (entrada && !salida) return false;
  return true;
}

function showSequenceErrorModal(colabNombre, validation) {
  const existing = document.getElementById('seq-error-modal');
  if (existing) existing.remove();
  const modal = document.createElement('div');
  modal.id = 'seq-error-modal';
  modal.className = 'modal-overlay';
  modal.innerHTML = `
    <div class="ops-validation-modal">
      <h3>&#9888; Secuencia invalida</h3>
      <p><strong>${colabNombre}</strong></p>
      <p>${validation.error}</p>
      <div style="display:flex;justify-content:flex-end;gap:8px;margin-top:12px">
        <button class="btn btn-primary btn-sm" id="seq-error-close">Entendido</button>
      </div>
    </div>`;
  document.body.appendChild(modal);
  document.getElementById('seq-error-close').onclick = () => modal.remove();
  modal.addEventListener('click', (e) => { if (e.target === modal) modal.remove(); });
}

// ── Render ────────────────────────────────────────────────────────────────────

function renderResumenRow(r) {
  // Determinar proxima accion para el boton admin
  // FRONT-VISTAS-EC-V04-T1: cada boton dice que registra (antes solo un icono: \u2b06 \ud83c\udf7d \u25b6 \u2b07), a 14px.
  const boton = (tipo) => `<button class="btn-sm btn-marcar" data-id="${r.colaboradorId}" data-nombre="${r.nombreColaborador}"
      data-tipo="${tipo}" title="${TIPO_INFO[tipo].label}" style="background:${TIPO_INFO[tipo].color};font-size:14px">${TIPO_INFO[tipo].icon} ${CORTO[tipo]}</button>`;
  const botones = ['entrada', 'inicio_almuerzo', 'fin_almuerzo', 'salida'].map(boton).join('');
  return `
    <div class="artifact-item asistencia-row" data-colab-id="${r.colaboradorId}">
      <div class="task-info">
        <div class="artifact-item-title">${r.nombreColaborador}</div>
        <div class="artifact-item-meta">
          <span>${r.cargoColaborador || '\u2014'}</span>
          ${r.ultimaEntrada ? `&middot; Entrada: <b>${fmt(r.ultimaEntrada)}</b>` : ''}
          ${r.ultimaSalida  ? `&middot; Salida: <b>${fmt(r.ultimaSalida)}</b>` : ''}
          ${r.horasTrabajadas != null ? `&middot; ${fmtHoras(r.horasTrabajadas)}` : ''}
        </div>
      </div>
      <div class="task-actions">
        ${estadoBadge(r.estadoActual)}
        ${botones}
      </div>
    </div>`;
}

function renderHistorialRow(a) {
  const tipoClass = tipoBadgeCls(a.tipo);
  const tipoLabelTxt = tipoLabel(a.tipo);
  const tsIso = a.timestampEvento ? new Date(a.timestampEvento).toISOString().slice(0, 16) : '';
  // ASIST-API (bit #4438, nexo-api#152): el GET ya expone fueCorregido/timestampEfectivo/
  // vecesCorregido desde v_asistencia_efectiva. Patron disponibilidad: si el backend aun
  // no los manda (deploy pendiente), el render queda EXACTAMENTE como antes — sin sello.
  const corregida = a.fueCorregido === true;
  const tsEfectivo = corregida ? (a.timestampEfectivo || null) : null;
  const veces = a.vecesCorregido ?? null;
  const selloCorregida = corregida
    ? ` <span class="badge badge-info" style="font-size:0.875rem"
          title="Marca original: ${fmt(a.timestampEvento)}${veces ? ` · ${veces} correccion(es)` : ''} · auditada (quien, cuando, motivo) en attendance_correcciones">✎ corregida</span>`
    : '';
  return `
    <div class="artifact-item" data-asistencia-id="${a.id}">
      <div class="task-info">
        <div class="artifact-item-title">${a.nombreColaborador || a.colaboradorId}</div>
        <div class="artifact-item-meta">
          <span class="asistencia-ts">${fmt(tsEfectivo || a.timestampEvento)}</span>${selloCorregida}
          ${a.notas ? `&middot; <span class="asistencia-notas">${a.notas}</span>` : ''}
          ${a.registradoPor ? `&middot; por ${a.registradoPor}` : ''}
          ${a.metodo && a.metodo !== 'manual' ? `&middot; <span class="badge badge-muted">${a.metodo}</span>` : ''}
        </div>
      </div>
      <div class="task-actions">
        <span class="badge ${tipoClass}">${tipoLabelTxt}</span>
        <button class="btn-icon btn-edit-asistencia" data-id="${a.id}" data-ts="${tsIso}"
          data-notas="${a.notas || ''}"          title="Corregir hora/notas">&#9998;</button>
        <button class="btn-icon btn-delete-asistencia" data-id="${a.id}"
          title="Eliminar registro" style="color:var(--color-danger,var(--accent-red))">&#128465;</button>
      </div>
    </div>`;
}

// ── Modal de edicion (admin corrige hora) ────────────────────────────────────

function showEditAsistenciaModal(businessId, id, currentTs, currentNotas, onDone) {
  const existing = document.getElementById('edit-asistencia-modal');
  if (existing) existing.remove();

  const modal = document.createElement('div');
  modal.id = 'edit-asistencia-modal';
  modal.className = 'modal-overlay';
  modal.innerHTML = `
    <div class="modal-box" style="max-width:460px">
      <div class="modal-header">
        <span class="modal-title">&#9998; Corregir registro de asistencia</span>
        <button class="modal-close" id="edit-asi-close">&#x2715;</button>
      </div>
      <div class="modal-body">
        <p style="font-size:0.875rem;color:var(--color-text-muted,var(--text-secondary));margin-bottom:1rem">
          Ajusta la hora registrada o las notas. Solo el admin puede hacer esto.
          Si cambias la hora, <strong>el motivo es obligatorio</strong> y la correccion
          queda auditada (quien, cuando, valor anterior) — <strong>la marca original
          nunca se pierde</strong>.
        </p>
        <label class="form-label">Fecha y hora del evento</label>
        <input type="datetime-local" id="edit-asi-ts" class="form-control" value="${currentTs}">
        <label class="form-label" style="margin-top:0.75rem">Motivo (obligatorio si cambias la hora, minimo 5 caracteres)</label>
        <input type="text" id="edit-asi-motivo" class="form-control"
          placeholder="Ej: Colaborador olvido marcar salida, se corrige a 18:00">
        <label class="form-label" style="margin-top:0.75rem">Notas actuales (opcional, se conservan)</label>
        <textarea id="edit-asi-notas" class="form-control" rows="2">${currentNotas}</textarea>
        <p id="edit-asi-error" class="auth-error" style="display:none;margin-top:0.5rem"></p>
      </div>
      <div class="modal-footer">
        <button class="btn btn-ghost" id="edit-asi-cancel">Cancelar</button>
        <button class="btn btn-primary" id="edit-asi-save">Guardar correccion</button>
      </div>
    </div>`;

  document.body.appendChild(modal);

  const close = () => modal.remove();
  document.getElementById('edit-asi-close').onclick = close;
  document.getElementById('edit-asi-cancel').onclick = close;

  document.getElementById('edit-asi-save').onclick = async () => {
    const ts = document.getElementById('edit-asi-ts').value;
    const motivo = document.getElementById('edit-asi-motivo').value.trim();
    const notasBase = document.getElementById('edit-asi-notas').value.trim();
    const $err = document.getElementById('edit-asi-error');
    $err.style.display = 'none';

    if (!ts) { $err.textContent = 'La fecha/hora es requerida'; $err.style.display = ''; return; }

    // ASIST-FIX-VISTA (contrato bit #4249): la HORA va por el endpoint de correccion
    // auditada (attendance_correcciones: quien, cuando, motivo, valor anterior; la
    // marca original NUNCA se pisa). El PATCH viejo queda SOLO para notas — su uso
    // anterior aqui (timestampEvento + marca [correccion...] en notas) borraba el
    // original: exactamente el hueco que ASIST-FIX cerro.
    const horaCambio = new Date(ts).toISOString() !== new Date(currentTs).toISOString();
    const notasCambio = notasBase !== String(currentNotas || '').trim();

    if (horaCambio && motivo.length < 5) {
      $err.textContent = 'El motivo es obligatorio para corregir la hora (minimo 5 caracteres)';
      $err.style.display = ''; return;
    }
    if (!horaCambio && !notasCambio) {
      $err.textContent = 'No cambiaste ni la hora ni las notas'; $err.style.display = ''; return;
    }

    const btn = document.getElementById('edit-asi-save');
    btn.disabled = true; btn.textContent = 'Guardando...';
    try {
      if (horaCambio) {
        unwrap(await AsistenciaApiClient.correccion(businessId, id, {
          timestampCorregido: new Date(ts).toISOString(),
          // #17507: sin corregidoPor; quien corrige lo pone la API desde el token.
          motivo
        }), 'la API rechazo la correccion');
      }
      if (notasCambio) {
        // Las notas viajan SOLAS: la hora nunca va por este PATCH (eso es /correccion, auditada).
        // Medido 16-oct (#28569): la API servida siempre manda fueCorregido, asi que la rama vieja que
        // re-mandaba timestampEvento (defensa de #4423 para el backend anterior a api#152) ya no corria;
        // se quita para que el 400 de A al PATCH con hora no la encuentre nunca.
        unwrap(await AsistenciaApiClient.patch(businessId, id, { notas: notasBase }),
          'la API rechazo la actualizacion de notas');
      }
      close();
      onDone();
    } catch (err) {
      btn.disabled = false; btn.textContent = 'Guardar correccion';
      $err.textContent = 'Error al guardar: ' + (err.message || 'intenta de nuevo');
      $err.style.display = '';
    }
  };
}

// ── Modal de registro ─────────────────────────────────────────────────────────

function showModal(businessId, colaboradorId, tipo, nombre, onDone, aviso = null) {
  const existing = document.getElementById('asistencia-modal');
  if (existing) existing.remove();

  const modal = document.createElement('div');
  modal.id = 'asistencia-modal';
  modal.className = 'modal-overlay';
  modal.innerHTML = `
    <div class="modal-box" style="max-width:420px">
      <div class="modal-header">
        <span class="modal-title">${TIPO_INFO[tipo]?.icon || ''} Registrar ${TIPO_INFO[tipo]?.label || tipo}</span>
        <button class="modal-close" id="modal-close-btn">✕</button>
      </div>
      <div class="modal-body">
        <p style="margin-bottom:0.75rem">
          Colaborador: <strong>${nombre}</strong>
        </p>
        ${aviso ? `<div data-asis-aviso style="margin-bottom:0.75rem;padding:8px 10px;font-size:14px;color:var(--accent-yellow);border:1px solid #d2992255;border-left:3px solid var(--accent-yellow);border-radius:4px">Advertencia (modo adaptación): ${esc(String(aviso).replace(/[.\s]+$/, ''))}. Puedes confirmar igual.</div>` : ''}
        <div data-asis-error role="alert" style="display:none;margin-bottom:0.75rem;font-size:14px;color:var(--accent-red)"></div>
        <label class="form-label">Notas (opcional)</label>
        <textarea id="modal-notas" class="form-control" rows="2" placeholder="Observaciones..."></textarea>
      </div>
      <div class="modal-footer">
        <button class="btn btn-ghost" id="modal-cancel-btn">Cancelar</button>
        <button class="btn btn-primary" id="modal-confirm-btn">Confirmar ${TIPO_INFO[tipo]?.label || tipo}</button>
      </div>
    </div>`;

  document.body.appendChild(modal);

  const close = () => modal.remove();
  document.getElementById('modal-close-btn').onclick = close;
  document.getElementById('modal-cancel-btn').onclick = close;

  document.getElementById('modal-confirm-btn').onclick = async () => {
    const notas = document.getElementById('modal-notas').value.trim() || undefined;
    const btn = document.getElementById('modal-confirm-btn');
    btn.disabled = true;
    btn.textContent = 'Guardando…';
    try {
      unwrap(await AsistenciaApiClient.registrar(businessId, { colaboradorId, tipo, notas }), 'la API rechazo el marcaje');
      close();
      onDone();
    } catch (err) {
      btn.disabled = false;
      btn.textContent = `Confirmar ${TIPO_INFO[tipo]?.label || tipo}`;
      // FRONT-VISTAS-EC-V04-T1: el error se dice en el modal, junto al boton (sin alert nativo).
      const $err = modal.querySelector('[data-asis-error]');
      if ($err) { $err.textContent = 'No se registró: ' + (err.message || 'intenta de nuevo') + '.'; $err.style.display = 'block'; }
    }
  };
}

// ── Vista principal ───────────────────────────────────────────────────────────

export async function renderAsistencia(container) {
  const businessId = getSelectedBusiness();
  const businessName = getSelectedBusinessName();

  if (!businessId) {
    container.innerHTML = `
      <div class="view-header"><h2 class="view-title">Control de Asistencia</h2></div>
      <div class="empty-state"><p>Selecciona un negocio en la barra superior para continuar.</p></div>`;
    return;
  }

  // PANEL-OPS-V2-001 T3 — Cargar config operativa del negocio desde la API.
  // Se cachea en window.nxBusinessConfig para que loadHoy la lea sin roundtrip.
  try {
    const cfgRes = await BusinessApiClient.getOpsConfig(businessId);
    if (cfgRes.ok) {
      const cfg = cfgRes.data?.data || cfgRes.data || {};
      window.nxBusinessConfig = {
        ...(window.nxBusinessConfig || {}),
        asistencia_strict_mode: !!cfg.asistencia_strict_mode,
        umbral_tardanza_minutos: Number(cfg.umbral_tardanza_minutos ?? 5),
        periodo_gracia_dias: Number(cfg.periodo_gracia_dias ?? 7)
      };
    }
  } catch { /* fallback a localStorage en loadHoy */ }

  // FRONT-VISTAS-EC-V04-T1: texto a 14px por AMBITO (data-asis-v04): las clases compartidas no cambian en otras vistas.
  container.innerHTML = `
    <style>
      [data-asis-v04] .business-scope-banner *, [data-asis-v04] .btn-sm, [data-asis-v04] .btn-filter, [data-asis-v04] .artifact-item-meta,
      [data-asis-v04] .artifact-item-meta *, [data-asis-v04] .ops-att-stat__label, [data-asis-v04] .badge, [data-asis-v04] summary,
      [data-asis-v04] .artifact-item-title, [data-asis-v04] details div, [data-asis-v04] input, [data-asis-v04] select, [data-asis-v04] .tab-btn { font-size:14px }
    </style>
    <div data-asis-v04>
    <div class="view-header">
      <div>
        <h2 class="view-title">Control de Asistencia</h2>
        <div class="business-scope-banner" style="margin-top:0.4rem">
          <span class="business-scope-icon">🏢</span>
          <span class="business-scope-label">Negocio activo:</span>
          <span class="business-scope-name">${businessName}</span>
          <span class="business-scope-hint">Solo colaboradores de este negocio</span>
        </div>
      </div>
      <div style="display:flex;gap:0.5rem;flex-wrap:wrap">
        <a href="#/marcacion" class="btn btn-primary btn-sm">&#9200; Abrir Kiosk</a>
        <a href="#/auditoria-asistencia" class="btn btn-ghost btn-sm">&#128202; Auditoria</a>
        <a href="#/horarios" class="btn btn-ghost btn-sm">&#128197; Horarios</a>
        <button class="btn btn-ghost btn-sm" id="btn-refresh-asistencia" title="Actualizar">&#8635; Actualizar</button>
      </div>
    </div>

    <div class="asistencia-tabs">
      <button class="tab-btn tab-active" data-tab="hoy">Hoy</button>
      <button class="tab-btn" data-tab="historial">Historial</button>
      <button class="tab-btn" data-tab="propuestas" title="Marcas que Argemiro propone para completar días incompletos">Por autorizar</button>
    </div>

    <div id="tab-hoy" class="tab-content">
      <div class="loading-state">Cargando estado del día…</div>
    </div>
    <div id="tab-historial" class="tab-content" style="display:none">
      <div class="asistencia-filtros" style="margin-bottom:1rem;display:flex;gap:0.5rem;flex-wrap:wrap;align-items:center">
        <input type="date" id="filtro-fecha" class="form-control" style="max-width:160px"
          value="${hoyColombia()}">
        <select id="filtro-tipo" class="form-control" style="max-width:180px">
          <option value="">Todos</option>
          <option value="entrada">⬆ Entradas</option>
          <option value="inicio_almuerzo">🍽 Salida a almuerzo</option>
          <option value="fin_almuerzo">🔄 Regreso almuerzo</option>
          <option value="salida">⬇ Salidas finales</option>
        </select>
        <button class="btn btn-sm btn-ghost" id="btn-buscar-historial">Buscar</button>
      </div>
      <div id="historial-list"><div class="loading-state">Cargando historial…</div></div>
    </div>
    <div id="tab-propuestas" class="tab-content" style="display:none"></div>
    </div>`;

  // ── Tabs ─────────────────────────────────────────────────────────────────

  let activeTab = 'hoy';
  container.querySelectorAll('.tab-btn').forEach(btn => {
    btn.addEventListener('click', () => {
      activeTab = btn.dataset.tab;
      container.querySelectorAll('.tab-btn').forEach(b => b.classList.remove('tab-active'));
      btn.classList.add('tab-active');
      container.querySelectorAll('.tab-content').forEach(c => c.style.display = 'none');
      document.getElementById(`tab-${activeTab}`).style.display = '';
      if (activeTab === 'historial') loadHistorial();
      // ARG-MARCAJE-PROPONE-T1: se carga al abrir la pestana (no en cada visita a Asistencia).
      if (activeTab === 'propuestas') renderMarcasPropuestas(document.getElementById('tab-propuestas'), businessId);
    });
  });

  // ── Carga resumen día ────────────────────────────────────────────────────

  async function loadHoy() {
    const el = document.getElementById('tab-hoy');
    if (!el) return;
    el.innerHTML = `<div class="loading-state">Cargando estado del día…</div>`;
    try {
      const res = await AsistenciaApiClient.resumenHoy(businessId);
      const items = res.data?.data ?? res.data ?? [];
      if (!items.length) {
        el.innerHTML = `<div class="empty-state"><p>No hay colaboradores activos registrados.</p></div>`;
        return;
      }
      // Flag de modo estricto: negocio puede desactivar bloqueos durante la primera semana.
      // Default false (el sistema solo advierte, no bloquea) durante periodo de adaptacion.
      const strictMode = !!(
        window.nxBusinessConfig?.asistencia_strict_mode
        ?? localStorage.getItem('nx_asistencia_strict_mode') === 'true'
      );

      // Calculos de dashboard — umbral de tardanza parametrizable (T3)
      const umbralTardanza = Number(window.nxBusinessConfig?.umbral_tardanza_minutos ?? 5);
      const presentes = items.filter(r => r.estadoActual === 'presente').length;
      const ausentes  = items.length - presentes;
      const tarde     = items.filter(r => r.minutosTarde != null && r.minutosTarde > umbralTardanza).length;
      const incompletos = items.filter(r => !tieneParCompleto(r)).length;

      const strictBadge = strictMode
        ? `<span class="badge badge-ok" style="margin-left:6px" title="Las secuencias invalidas se bloquean">Modo estricto</span>`
        : `<span class="badge badge-muted" style="margin-left:6px" title="Periodo de adaptacion: secuencias invalidas muestran warning pero no bloquean">Modo adaptacion</span>`;

      el.innerHTML = `
        <div style="display:flex;justify-content:space-between;align-items:center;flex-wrap:wrap;gap:6px;margin-bottom:10px">
          <h3 style="margin:0;font-size:0.95rem">Dashboard Hoy ${strictBadge}</h3>
        </div>
        <div class="ops-att-grid">
          <div class="ops-att-stat ops-att-stat--ok">
            <div class="ops-att-stat__val" style="color:var(--accent-green-text)">${presentes}</div>
            <div class="ops-att-stat__label">Presentes</div>
          </div>
          <div class="ops-att-stat ops-att-stat--warn">
            <div class="ops-att-stat__val" style="color:var(--accent-yellow)">${tarde}</div>
            <div class="ops-att-stat__label">Tarde (>${umbralTardanza}m)</div>
          </div>
          <div class="ops-att-stat ops-att-stat--err">
            <div class="ops-att-stat__val" style="color:var(--accent-red)">${ausentes}</div>
            <div class="ops-att-stat__label">Ausentes</div>
          </div>
          <div class="ops-att-stat ops-att-stat--info">
            <div class="ops-att-stat__val" style="color:var(--accent-blue)">${incompletos}</div>
            <div class="ops-att-stat__label">Incompletos</div>
          </div>
        </div>
        ${incompletos > 0 ? `
          <details style="margin-bottom:10px;background:var(--bg-card);border:1px solid var(--border-default);border-radius:6px;padding:8px 12px">
            <summary style="cursor:pointer;font-size:14px;color:var(--accent-yellow);font-weight:600">
              Colaboradores con registro incompleto (${incompletos})
            </summary>
            <div style="margin-top:6px;font-size:0.875rem;color:var(--text-secondary)">
              ${items.filter(r => !tieneParCompleto(r)).map(r => `
                <div style="padding:4px 0">
                  &bull; <strong>${r.nombreColaborador}</strong> —
                  ${r.ultimaEntrada && !r.ultimaSalida ? `entrada a las ${fmt(r.ultimaEntrada)}, sin salida` : ''}
                  ${(r.ultimoInicioAlmuerzo || r.inicioAlmuerzo) && !(r.ultimoFinAlmuerzo || r.finAlmuerzo) ? `inicio almuerzo ${fmt(r.ultimoInicioAlmuerzo || r.inicioAlmuerzo)}, sin fin_almuerzo` : ''}
                </div>`).join('')}
            </div>
          </details>` : ''}

        <div id="resumen-list" class="artifact-list">
          ${items.map(renderResumenRow).join('')}
        </div>
        <!-- FRONT-VISTAS-EC-V04-T1: el modo estricto cambia una regla del NEGOCIO; antes estaba pegado al resumen del dia,
             al lado de lo que se viene a mirar. Queda plegado en Ajustes, debajo, y dice que hace. -->
        <details data-asis-ajustes style="margin-top:14px;font-size:14px">
          <summary style="cursor:pointer;color:var(--text-secondary)">Ajustes de asistencia</summary>
          <div style="margin-top:8px;color:var(--text-secondary)">${strictMode
            ? 'Modo estricto: una marca fuera de orden se bloquea.'
            : 'Modo adaptación: una marca fuera de orden se avisa pero se puede registrar.'}</div>
          <button class="btn btn-ghost btn-sm" id="nx-toggle-strict" style="margin-top:8px;font-size:14px">
            ${strictMode ? 'Pasar a modo adaptación' : 'Pasar a modo estricto'}
          </button>
        </details>`;

      // Toggle strict mode — ahora persiste en el negocio via API (T3).
      // Fallback a localStorage si el PATCH falla (red o permisos).
      document.getElementById('nx-toggle-strict')?.addEventListener('click', async () => {
        const newVal = !strictMode;
        const btn = document.getElementById('nx-toggle-strict');
        if (btn) { btn.disabled = true; btn.textContent = 'Guardando...'; }
        try {
          const res = await BusinessApiClient.updateOpsConfig(businessId, { asistencia_strict_mode: newVal });
          if (res.ok) {
            window.nxBusinessConfig = { ...(window.nxBusinessConfig || {}), asistencia_strict_mode: newVal };
          } else {
            // Mantener fallback local si el endpoint no esta listo
            localStorage.setItem('nx_asistencia_strict_mode', String(newVal));
          }
        } catch {
          localStorage.setItem('nx_asistencia_strict_mode', String(newVal));
        }
        loadHoy();
      });

      // Botones de marcacion (los 4 tipos) con validacion client-side
      el.querySelectorAll('.btn-marcar').forEach(btn => {
        btn.addEventListener('click', () => {
          const id = btn.dataset.id;
          const tipo = btn.dataset.tipo;
          const nombre = btn.dataset.nombre;
          const row = items.find(r => String(r.colaboradorId) === String(id));
          let aviso = null;
          if (row) {
            const v = validarSecuencia(row, tipo);
            if (!v.ok) {
              if (strictMode) {
                showSequenceErrorModal(nombre, v);
                return;
              }
              // periodo de adaptacion: warning no bloqueante. FRONT-VISTAS-EC-V04-T1: va DENTRO del modal de marcaje
              // (antes era un confirm() nativo): Cancelar o Confirmar ya estan ahi.
              aviso = v.error;
            }
          }
          showModal(businessId, id, tipo, nombre, loadHoy, aviso);
        });
      });
    } catch (err) {
      el.innerHTML = `<div class="empty-state"><p>Error cargando datos: ${err.message}</p></div>`;
    }
  }

  // ── Carga historial ──────────────────────────────────────────────────────

  async function loadHistorial() {
    const el = document.getElementById('historial-list');
    if (!el) return;
    el.innerHTML = `<div class="loading-state">Cargando…</div>`;

    const fecha = document.getElementById('filtro-fecha')?.value;
    const tipo = document.getElementById('filtro-tipo')?.value;

    const opts = { pageSize: 50 };
    if (tipo) opts.tipo = tipo;
    if (fecha) {
      // Rango del dia en hora Colombia (UTC-5) para evitar el bug de saltar al dia siguiente
      const r = rangoColombia(fecha);
      opts.fechaDesde = r.fechaDesde;
      opts.fechaHasta = r.fechaHasta;
    }

    try {
      const res = await AsistenciaApiClient.list(businessId, opts);
      const items = res.data?.data?.items ?? res.data?.items ?? [];
      if (!items.length) {
        el.innerHTML = `<div class="empty-state"><p>Sin registros para los filtros seleccionados.</p></div>`;
        return;
      }
      el.innerHTML = `<div class="artifact-list">${items.map(renderHistorialRow).join('')}</div>`;

      // Botones editar y eliminar en cada fila
      el.querySelectorAll('.btn-edit-asistencia').forEach(btn => {
        btn.addEventListener('click', () => {
          showEditAsistenciaModal(businessId, btn.dataset.id, btn.dataset.ts, btn.dataset.notas, loadHistorial);
        });
      });
      // FRONT-VISTAS-EC-V04-T1: eliminar confirma EN LA FILA (antes confirm() nativo) y el error se dice ahi (antes alert()).
      el.querySelectorAll('.btn-delete-asistencia').forEach(btn => {
        btn.addEventListener('click', () => {
          const row = btn.closest('[data-asistencia-id]');
          if (!row || row.querySelector('[data-asis-borrar]')) return;
          const nombre = row.querySelector('.artifact-item-title')?.textContent || 'este registro';
          const ts = row.querySelector('.asistencia-ts')?.textContent || '';
          const $c = document.createElement('div');
          $c.setAttribute('data-asis-borrar', '');
          $c.style.cssText = 'flex-basis:100%;margin-top:8px;font-size:14px;color:var(--text-heading)';
          $c.innerHTML = `<span>¿Eliminar el registro de ${esc(nombre)}${ts ? ` (${esc(ts)})` : ''}?</span>
            <button type="button" class="btn btn-sm" data-asis-borrar-si style="margin-left:8px;font-size:14px;background:#da3633;color:var(--text-on-accent)">Sí, eliminar</button>
            <button type="button" class="btn btn-ghost btn-sm" data-asis-borrar-no style="margin-left:4px;font-size:14px">Cancelar</button>
            <div data-asis-borrar-error role="alert" style="color:var(--accent-red);margin-top:4px"></div>`;
          row.appendChild($c);
          $c.querySelector('[data-asis-borrar-no]').addEventListener('click', () => { $c.remove(); btn.focus(); });
          const $si = $c.querySelector('[data-asis-borrar-si]');
          $si.focus();
          $si.addEventListener('click', async () => {
            $si.disabled = true;
            try {
              unwrap(await AsistenciaApiClient.delete(businessId, btn.dataset.id), 'la API rechazo la eliminacion');
              loadHistorial();
            } catch (err) {
              $si.disabled = false;
              $c.querySelector('[data-asis-borrar-error]').textContent = 'No se eliminó: ' + (err.message || 'intenta de nuevo') + '.';
            }
          });
        });
      });
    } catch (err) {
      el.innerHTML = `<div class="empty-state"><p>Error: ${err.message}</p></div>`;
    }
  }

  // ── Eventos ──────────────────────────────────────────────────────────────

  document.getElementById('btn-refresh-asistencia')?.addEventListener('click', () => {
    if (activeTab === 'hoy') loadHoy();
    else loadHistorial();
  });
  document.getElementById('btn-buscar-historial')?.addEventListener('click', loadHistorial);

  // Listener para cambio de negocio
  function onBusinessChanged(e) {
    renderAsistencia(container);
  }
  window.addEventListener('business:changed', onBusinessChanged);
  container._cleanupBusinessListener = () => window.removeEventListener('business:changed', onBusinessChanged);

  // PANEL-OPS-V3-001 T3 — pull-to-refresh (solo touch devices)
  const $tabHoy = document.getElementById('tab-hoy');
  const $tabHist = document.getElementById('tab-historial');
  if ($tabHoy) attachPullToRefresh($tabHoy, async () => { if (activeTab === 'hoy') await loadHoy(); });
  if ($tabHist) attachPullToRefresh($tabHist, async () => { if (activeTab === 'historial') await loadHistorial(); });

  // Carga inicial
  loadHoy();
}
